import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { ProcurementTypeItemFragment, WorkflowContainerFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { Form } from 'modules/forms'

interface TabContentProps {
  form: Form | undefined
  id: string | undefined
  requestType: ProcurementTypeItemFragment | undefined
  setForm: (form: Form) => void
  workflow: WorkflowContainerFragment | undefined
}

const FormTab = React.lazy(() => import('modules/procurement/components/procurementTypes/Form'))
const WorkflowsTab = React.lazy(
  () => import('modules/procurement/components/procurementTypes/Workflows')
)

const TabContent = ({
  form,
  id,
  requestType,
  setForm,
  workflow,
}: TabContentProps): React.JSX.Element => {
  const { navigation } = useNavigation()
  const [searchParams] = useSearchParams()

  // The active tab is from the params or 'form' by default
  const activeTab = React.useMemo(() => searchParams.get('tab') || 'form', [searchParams])

  const Content = React.useMemo<React.ReactNode>(() => {
    const hasContent = requestType && id

    if (!hasContent) {
      return null
    }

    switch (activeTab) {
      case 'workflows':
        return <WorkflowsTab requestType={requestType} workflow={workflow ?? null} />
      case 'form':
      default:
        return <FormTab requestType={requestType} form={form} setForm={setForm} />
    }
  }, [activeTab, form, setForm, id, requestType, workflow])

  const path = React.useMemo(() => {
    return navigation.settings.procurement.requestType.id.fullPath({
      id: id || '',
    })
  }, [navigation, id])

  return (
    <>
      {/* Tabs */}
      <Tabs
        key={activeTab}
        activeTabId={activeTab}
        tabs={[
          {
            id: 'form',
            href: `${path}?tab=form`,
            label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
            index: true,
          },
          {
            id: 'workflows',
            href: `${path}?tab=workflows`,
            label: i18n.t(
              'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
            ),
          },
        ].filter(Boolean)}
      />

      {Content}
    </>
  )
}

export default React.memo(TabContent)
